/*
 * SPDX-FileCopyrightText: 2022-2024 CERN.
 * SPDX-FileCopyrightText: 2024 KTH Royal Institute of Technology.
 * SPDX-License-Identifier: MIT
 */

import { i18next } from "@translations/invenio_app_rdm/i18next";
import _get from "lodash/get";
import _truncate from "lodash/truncate";
import React, { Component } from "react";
import Overridable from "react-overridable";
import { SearchItemCreators } from "../utils";
import { SearchItemContributors } from "../utils";
import PropTypes from "prop-types";
import { Item, Label, Icon } from "semantic-ui-react";
import { buildUID } from "react-searchkit";
import { CompactStats } from "./CompactStats";
import { DisplayPartOfCommunities } from "./DisplayPartOfCommunities";

class RecordsResultsListItem extends Component {
  render() {
    const { currentQueryState, result, key, appName } = this.props;

    const accessStatusId = _get(result, "ui.access_status.id", "open");
    const accessStatus = _get(result, "ui.access_status.title_l10n", "Open");
    const accessStatusIcon = _get(result, "ui.access_status.icon", "unlock");
    const createdDate = _get(
      result,
      "ui.created_date_l10n_long",
      i18next.t("No creation date found.")
    );

    const creators = result.ui.creators.creators;
    const contributors = result.ui.contributors.contributors;

    const descriptionStripped = _get(
      result,
      "ui.description_stripped",
      i18next.t("No description")
    );

    const publicationDate = _get(
      result,
      "ui.publication_date_l10n_long",
      i18next.t("No publication date found.")
    );
    const resourceType = _get(
      result,
      "ui.resource_type.title_l10n",
      i18next.t("No resource type")
    );
    const resourceTypeId = _get(result, "ui.resource_type.id", "");

    const [mainType] = resourceTypeId.split("-");
    const filterValue = resourceTypeId.includes("-")
      ? `resource_type:${mainType}+inner:${resourceTypeId}`
      : `resource_type:${resourceTypeId}`;

    const resourceTypeFilter = encodeURIComponent(filterValue);

    const subjects = _get(result, "ui.subjects", []);
    const title = _get(result, "metadata.title", i18next.t("No title"));
    const titleTruncated = _truncate(title, { length: 100 });
    const version = _get(result, "ui.version", null);
    const versions = _get(result, "versions");
    const publisher = _get(result, "metadata.publisher", "");
    const category = _get(result, "custom_fields.rdm:category.id", "general");
    const defaultimg = _get(result, "files.default_preview", "");
    // Only use the IIIF thumbnail when the preview file is actually an image
    // (some records set default_preview to non-image files, e.g. .md notes;
    // the IIIF endpoint 404s on those).
    const hasImagePreview =
      typeof defaultimg === "string" &&
      defaultimg.length > 0 &&
      /\.(png|jpe?g|gif|webp|tiff?|bmp)$/i.test(defaultimg);

    const publishingInformation = _get(result, "ui.publishing_information.journal", "");

    const filters = currentQueryState && Object.fromEntries(currentQueryState.filters);
    const allVersionsVisible = filters?.allversions;
    const numOtherVersions = versions.index - 1;

    // Derivatives
    const viewLink = `/records/${result.id}`;
    const viewThumbshort = `api/iiif/record:${result.id}:`;
    return (
      <Overridable
        id={buildUID("RecordsResultsListItem.layout", "", appName)}
        result={result}
        key={key}
        accessStatusId={accessStatusId}
        accessStatus={accessStatus}
        accessStatusIcon={accessStatusIcon}
        createdDate={createdDate}
        creators={creators}
        contributors={contributors}
        descriptionStripped={descriptionStripped}
        publicationDate={publicationDate}
        resourceType={resourceType}
        subjects={subjects}
        title={title}
        publisher={publisher}
        category={category}
        defaultimg={defaultimg}
        version={version}
        versions={versions}
        allVersionsVisible={allVersionsVisible}
        numOtherVersions={numOtherVersions}
        viewThumbshort={viewThumbshort}
      >
        <Item key={key ?? result.id}>
          <Item.Content>
            {/* FIXME: Uncomment to enable themed banner */}
            {/* <DisplayVerifiedCommunity communities={result.parent?.communities} /> */}
            <Item.Extra className="labels-actions">
              <Overridable
                id={buildUID("RecordsResultsListItem.labels", "", appName)}
                result={result}
              >
                <>
                  <Label horizontal size="small" className="primary theme-primary">
                    {publicationDate} ({version})
                  </Label>
                  <Label
                    horizontal
                    size="small"
                    className="neutral"
                    as="a"
                    href={`${window.location.pathname}?q=&f=${resourceTypeFilter}`}
                  >
                    {resourceType}
                  </Label>
                  <Label
                    horizontal
                    size="small"
                    className={`access-status ${accessStatusId}`}
                  >
                    {accessStatusIcon && <Icon name={accessStatusIcon} />}
                    {accessStatus}
                  </Label>
                </>
              </Overridable>
              <Overridable
                id={buildUID("RecordsResultsListItem.labels.after", "", appName)}
                result={result}
              />
            </Item.Extra>
            <Item.Header as="h2" className="theme-primary-text">
              <a href={viewLink}>{title}</a>
            </Item.Header>
            <Item className="creatibutors">
              <SearchItemCreators creators={creators} othersLink={viewLink} />
            </Item>
            <Item.Description>
              {_truncate(descriptionStripped, { length: 350 })}
              <br/><b>Keywords:</b> <SearchItemContributors contributors={contributors} othersLink={viewLink} />
            </Item.Description>

            <Item.Extra>
              {subjects.map((subject) => (
                <Label key={subject.title_l10n} size="tiny">
                  {subject.title_l10n}
                </Label>
              ))}

              <div className="flex justify-space-between align-items-end">
                <small>
                  <DisplayPartOfCommunities communities={result.parent?.communities} />
                  <p>
                    {createdDate && (
                      <>
                        {i18next.t("Uploaded on {{uploadDate}}", {
                          uploadDate: createdDate,
                        })}
                      </>
                    )}
                    {createdDate && publishingInformation && " | "}

                    {publishingInformation && (
                      <>
                        {i18next.t("Published in: {{- publishInfo }}", {
                          publishInfo: publishingInformation,
                        })}
                      </>
                    )}
                  </p>

                  {!allVersionsVisible && versions.index > 1 && (
                    <p>
                      <b>
                        {i18next.t("{{count}} more versions exist for this record", {
                          count: numOtherVersions,
                        })}
                      </b>
                    </p>
                  )}
                  <p>
                    <b>
                      <a href={`/search?q=custom_fields.rdm\\:category.id:"${category}"`}>Content category: {category}</a>
                    </b>
                  </p>

                </small>
                <small>
                  <p>
                    <b>
                      <a href={viewLink}>
                        {hasImagePreview ? (
                          <img
                            src={`https://www.compoid.com/${viewThumbshort}${defaultimg}/full/!300,300/0/default.png`}
                            alt="View Record"
                            loading="lazy"
                            onError={(e) => {
                              // IIIF 404 fallback (e.g. preview file deleted
                              // or not actually an image)
                              if (e.currentTarget.src.indexOf("view-record") === -1) {
                                e.currentTarget.src = "/static/images/view-record.png";
                              }
                            }}
                          />
                        ) : (
                          <img src="/static/images/view-record.png" alt="View Record" loading="lazy" />
                        )}
                      </a>
                    </b>
                  </p>
                </small>
              </div>
            </Item.Extra>
          </Item.Content>
        </Item>
      </Overridable>
    );
  }
}

RecordsResultsListItem.propTypes = {
  currentQueryState: PropTypes.object,
  result: PropTypes.object.isRequired,
  key: PropTypes.string,
  appName: PropTypes.string,
};

RecordsResultsListItem.defaultProps = {
  key: null,
  currentQueryState: null,
  appName: "",
};

export default Overridable.component("RecordsResultsListItem", RecordsResultsListItem);